import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCalendarAlt } from "react-icons/fa";
import styles from "../FeeType/styles.module.css";
import { CustomButton, CustomDropdown } from "../../../../components";
import {
  fetchClassOptions,
  fetchSectionOptions,
  clearSectionOptions,
} from "../../../../redux/Masterdata";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { useDispatch, useSelector } from "react-redux";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

const ASSIGN_VIEW_URL = "schoolFees/group/assign-view";
const ASSIGNED_STUDENTS_URL = "schoolFees/group/assigned-students";
const GROUP_ASSIGN_URL = "schoolFees/group-assign";
const GROUP_ASSIGN_EDIT_URL = "schoolFees/groupAssignEdit";
const UNASSIGN_URL = "schoolFees/feeGroup/unAssign";

// per-row status values sent to feeGroup/unAssign
const STATUS_ASSIGNED = 1;
const STATUS_UNASSIGN = 0;

/* Normalize whatever assign-view sends for a student's status into
   STATUS_ASSIGNED (0) / STATUS_UNASSIGN (1), so the dropdown selects
   the right option regardless of number vs string form.
   Accepts: 0/1, "0"/"1", "assigned"/"unassign", true/false.
   ⚠ Assumes 1 = assigned on the API side. If the backend uses the
   opposite convention, flip the two returns below. */
const normalizeStatus = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (s === "assigned" || s === "assign" || s === "1" || s === "true")
      return STATUS_ASSIGNED;
    if (s === "unassign" || s === "unassigned" || s === "0" || s === "false")
      return STATUS_UNASSIGN;
    return null;
  }
  // numeric / boolean: API 1 (assigned) -> STATUS_ASSIGNED
  return Number(raw) === 1 ? STATUS_ASSIGNED : STATUS_UNASSIGN;
};

// Static badge shown on the Add screen (no per-row unassign there).
const AssignedStatusBadge = ({ assigned }) => {
  const isAssigned = Number(assigned) === 1;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "6px 16px",
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 700,
        color: "#fff",
        whiteSpace: "nowrap",
        backgroundColor: isAssigned ? "#16a34a" : "#dc2626",
      }}
    >
      {isAssigned ? "Assigned" : "Not Assigned"}
    </span>
  );
};

const CreateFeeAssign = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);

  // ── Values coming from the List page (View button or Add button) ──
  const navMode = location.state?.mode ?? ""; // "view" | "add" | ""
  const viewClassId = location.state?.class_id ?? "";
  const viewClassName = location.state?.class_name ?? "";
  const viewSectionId = location.state?.section_id ?? "";
  const viewMedium = location.state?.medium ?? "";

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const master_id = clientData?.master_id || "";
  const academic_year_id =
    clientData?.academic_year_id || clientData?.academic_id || "";
  const academic_year = clientData?.academic_year || "";
  const user_id = user?.id || "";
  const roleId = Number(user?.role?.id);

  const {
    classOptions: reduxClassOptions,
    staffClassAndSubjectOptions,
    sectionOptions: reduxSectionOptions,
  } = useSelector((state) => state.masterData);

  const [data, setData] = useState([]);
  // selectedClass holds the class_id (matches the dropdown option value)
  const [selectedClass, setSelectedClass] = useState(viewClassId || "");
  const [selectedSection, setSelectedSection] = useState(viewSectionId || "");
  // View mode = arrived via the List "View" button. Any manual dropdown
  // change flips this off so we fall back to the "Add" students endpoint.
  const [viewMode, setViewMode] = useState(navMode === "view");
  const [submitting, setSubmitting] = useState(false);
  const [loading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [formData, setFormData] = useState({
    medium: viewMedium || "",
    groupType: "",
  });
  const [errors, setErrors] = useState({});
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  // ── Assigned-status filter, sent to the API as `status` ──
  // "" = all, "1" = Assigned, "0" = Not Assigned
  const [assignStatus, setAssignStatus] = useState("");
  // ── MRT-controlled column filters (native select UI, matches
  //    SecondaryAdmissionList's filterVariant:"select" columns) ──
  const [columnFilters, setColumnFilters] = useState([]);

  // ── Manual checkbox selection — keyed on student_id ──
  // student_id is the ONLY id present in BOTH API responses, and it's the
  // id both the create and update payloads expect.
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);

  // ── Group Type dropdown options (from GroupList API) ──
  const [groupTypeOptions, setGroupTypeOptions] = useState([]);
  const [groupTypeLoading, setGroupTypeLoading] = useState(false);

  // ── Per-row unassign: student_id currently being updated ──
  const [rowUpdatingId, setRowUpdatingId] = useState(null);

  // const [pagination, setPagination] = useState({
  //   pageIndex: 0,
  //   pageSize: 10,
  // });

  // ── Fetch class options on mount ──
  useEffect(() => {
    dispatch(
      fetchClassOptions({
        id: parseInt(school_id),
        academic_year: academic_year,
      }),
    );
  }, [dispatch, school_id]);

  // ── If we arrived with a class, load its sections so the Section
  //    dropdown can render the selected value ──
  useEffect(() => {
    if (viewClassId && school_id) {
      dispatch(
        fetchSectionOptions({
          id: [parseInt(viewClassId)],
          school_id: parseInt(school_id),
          staff_id: parseInt(master_id),
          subject_staff: 1,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch Group Type list (depends on class & section) ──
  const fetchGroupTypes = useCallback(async () => {
    if (!school_id || !academic_year_id) return;
    setGroupTypeLoading(true);
    try {
      const response = await AxiosInstance.post(
        "schoolFees/feeGroup/GroupList",
        {
          school_id: Number(school_id),
          academic_year_id: Number(academic_year_id),
          class_id: selectedClass || "",
          section_id: selectedSection || "",
          search: "",
          page: "",
          limit: "",
        },
      );

      if (response?.data?.status && Array.isArray(response.data.data)) {
        setGroupTypeOptions(
          response.data.data.map((item) => ({
            value: String(item.id),
            label: item.group_name,
          })),
        );
      } else {
        setGroupTypeOptions([]);
      }
    } catch (error) {
      console.error("Error fetching group types:", error);
      setGroupTypeOptions([]);
    } finally {
      setGroupTypeLoading(false);
    }
  }, [school_id, academic_year_id, selectedClass, selectedSection]);

  useEffect(() => {
    fetchGroupTypes();
  }, [fetchGroupTypes]);

  const fetchStudentList = useCallback(async () => {
    if (!school_id || !selectedClass || !selectedSection) {
      setData([]);
      return;
    }
    setTableLoading(true);

    const url = viewMode ? ASSIGN_VIEW_URL : ASSIGNED_STUDENTS_URL;

    try {
      const payload = {
        school_id: Number(school_id),
        academic_id: Number(academic_year_id),
        class_id: Number(selectedClass),
        section_id: String(selectedSection),
      };
      if (assignStatus !== "") {
        payload.status = Number(assignStatus);
      }

      const response = await AxiosInstance.post(url, payload);

      const list = response?.data?.data;
      const ok = response?.data?.status || response?.data?.success;

      if (ok && Array.isArray(list)) {
        const normalized = viewMode
          ? list.map((item) => ({
              student_id: item.student_id,
              user_id: item.user_id ?? null,
              student_name: item.student_name || "",
              assign_id: item.assign_id ?? null,
              group_id: item.group_id,
              group_name: item.group_name,
              group_fees: item.group_name || "-",
              is_group: item.is_assigned,
              row_status: normalizeStatus(
                item.status ?? item.assign_status ?? item.is_assigned,
              ),
              assigned_status:
                Number(item.is_assigned) === 1 ? "Assigned" : "Not Assigned",
            }))
          : list.map((item) => ({
              student_id: item.student_id,
              user_id: item.user_id,
              student_name: item.student_name || "",
              student_class: item.student_class,
              student_section: item.student_section,
              class_name: item.class_name,
              group_id: item.id ?? null,
              group_name: item.group_name || "",
              group_fees: item.group_name || "-",
              assign_id: null, // not assigned yet
              is_group: item.is_group,
              assigned_status:
                Number(item.is_group) === 1 ? "Assigned" : "Not Assigned",
            }));

        setData(normalized);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching student list:", error);
      setData([]);
    } finally {
      setTableLoading(false);
    }
  }, [
    school_id,
    selectedClass,
    selectedSection,
    academic_year_id,
    viewMode,
    assignStatus,
  ]);

  useEffect(() => {
    fetchStudentList();
  }, [fetchStudentList]);

  // ── Clear selection when class/section/search/status changes ──
  useEffect(() => {
    setSelectedStudentIds([]);
  }, [selectedClass, selectedSection, apiSearch, assignStatus]);

  // ── Assigned-status filter options ──
  const assignStatusOptions = useMemo(
    () => [
      {
        value: "",
        label:
          translations.labels?.filterByAssignedStatus ||
          "Filter by Assigned Status",
      },
      { value: "1", label: translations.labels?.assigned || "Assigned" },
      {
        value: "0",
        label: translations.labels?.notAssigned || "Not Assigned",
      },
    ],
    [translations],
  );

  // ── Sync MRT's native filter select into `assignStatus` (drives the
  //    API call in fetchStudentList) — same controlled-columnFilters
  //    pattern SecondaryAdmissionList uses for its Status/Gender filters. ──
  useEffect(() => {
    const statusFilter = columnFilters.find((f) => f.id === "assigned_status");
    const status = statusFilter?.value ?? "";
    setAssignStatus((prev) => (prev === status ? prev : status));
    // setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, [columnFilters]);

  // ── Reset group type when class/section changes ──
  useEffect(() => {
    setFormData((prev) => ({ ...prev, groupType: "" }));
  }, [selectedClass, selectedSection]);

  const sectionOptions = useMemo(() => {
    const base = [
      {
        value: "",
        label: translations.labels?.selectSection || "Select Section",
      },
      ...reduxSectionOptions,
    ];
    if (
      viewSectionId &&
      !base.some((o) => String(o.value) === String(viewSectionId))
    ) {
      base.push({ value: viewSectionId, label: String(viewSectionId) });
    }
    return base;
  }, [reduxSectionOptions, translations, viewSectionId]);

  const classOptions = useMemo(() => {
    const sourceData =
      roleId === 3 ? staffClassAndSubjectOptions : reduxClassOptions;

    const base = [
      {
        value: "",
        label: translations.labels?.selectClass || "Select Class",
      },
      ...sourceData.map((item) => ({
        value: item.class_id ?? item.value,
        label: item.class_name ?? item.label,
      })),
    ];
    if (
      viewClassId &&
      !base.some((o) => String(o.value) === String(viewClassId))
    ) {
      base.push({
        value: viewClassId,
        label: viewClassName || String(viewClassId),
      });
    }
    return base;
  }, [
    reduxClassOptions,
    staffClassAndSubjectOptions,
    roleId,
    translations,
    viewClassId,
    viewClassName,
  ]);

  // ── Snap the prefilled class/section to the EXACT option value so the
  //    dropdown's strict compare renders the label (number vs string). ──
  useEffect(() => {
    if (!selectedClass) return;
    const match = classOptions.find(
      (o) => o.value !== "" && String(o.value) === String(selectedClass),
    );
    if (match && match.value !== selectedClass) setSelectedClass(match.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classOptions]);

  useEffect(() => {
    if (!selectedSection) return;
    const match = sectionOptions.find(
      (o) => o.value !== "" && String(o.value) === String(selectedSection),
    );
    if (match && match.value !== selectedSection)
      setSelectedSection(match.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionOptions]);

  const handleSectionChange = useCallback((value) => {
    const sectionId = typeof value === "object" ? value?.value : value;
    setSelectedSection(sectionId);
    setViewMode(false);
    // setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleClassChange = useCallback(
    (value) => {
      const classId = typeof value === "object" ? value?.value : value;
      setSelectedClass(classId);
      setSelectedSection("");
      setViewMode(false);

      dispatch(clearSectionOptions());

      if (classId) {
        dispatch(
          fetchSectionOptions({
            id: [parseInt(classId)],
            school_id: parseInt(school_id),
            staff_id: parseInt(master_id),
            subject_staff: 1,
          }),
        );
      }
      // setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch, school_id, master_id],
  );

  const handleDropdownChange = (name, value) => {
    const finalValue =
      value && typeof value === "object"
        ? (value.value ?? value.target?.value ?? "")
        : value;

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ── Checkbox handlers (keyed on student_id) ──
  // No rows are disabled — every student in the list is selectable.
  const handleToggleStudent = useCallback((studentId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  }, []);

  const handleToggleAll = useCallback(() => {
    const currentIds = data.map((row) => row.student_id);
    const allSelected =
      currentIds.length > 0 &&
      currentIds.every((id) => selectedStudentIds.includes(id));

    if (allSelected) {
      setSelectedStudentIds((prev) =>
        prev.filter((id) => !currentIds.includes(id)),
      );
    } else {
      setSelectedStudentIds((prev) => [...new Set([...prev, ...currentIds])]);
    }
  }, [data, selectedStudentIds]);

  const isAllCurrentPageSelected = useMemo(() => {
    if (data.length === 0) return false;
    return data.every((row) => selectedStudentIds.includes(row.student_id));
  }, [data, selectedStudentIds]);

  const isSomeCurrentPageSelected = useMemo(() => {
    if (data.length === 0) return false;
    const some = data.some((row) =>
      selectedStudentIds.includes(row.student_id),
    );
    return some && !isAllCurrentPageSelected;
  }, [data, selectedStudentIds, isAllCurrentPageSelected]);

  const validateForm = () => {
    const newErrors = {};

    if (!selectedClass) {
      newErrors.class =
        translations.labels?.classRequired || "Class is required";
    }
    if (!selectedSection) {
      newErrors.section =
        translations.labels?.sectionRequired || "Section is required";
    }
    if (!formData.groupType) {
      newErrors.groupType =
        translations.labels?.feeTypeRequired || "Group type is required";
    }
    if (selectedStudentIds.length === 0) {
      newErrors.students = "students";
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.selectAtLeastOneStudent ||
          "Please select at least one student",
        redirectPath: "",
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRowStatusChange = useCallback(
    async (row, nextStatus) => {
      if (!row?.assign_id) {
        setModalState({
          open: true,
          type: "error",
          message:
            "This student has no assignment id yet, so the status can't be changed.",
          redirectPath: "",
        });
        return;
      }

      setRowUpdatingId(row.student_id);
      try {
        const response = await AxiosInstance.post(UNASSIGN_URL, {
          assign_id: Number(row.assign_id),
          status: Number(nextStatus),
          updated_by: Number(user_id),
        });

        const ok = response?.data?.status || response?.data?.success;

        if (ok) {
          setModalState({
            open: true,
            type: "success",
            message:
              response?.data?.message ||
              (Number(nextStatus) === STATUS_UNASSIGN
                ? "Student unassigned successfully!"
                : "Student assigned successfully!"),
            redirectPath: "",
          });
          fetchStudentList(); // reflect the new state
        } else {
          setModalState({
            open: true,
            type: "error",
            message:
              response?.data?.message ||
              translations.labels?.somethingWrong ||
              "Something went wrong!",
            redirectPath: "",
          });
        }
      } catch (error) {
        console.error("Error updating assignment status:", error);
        setModalState({
          open: true,
          type: "error",
          message:
            error?.response?.data?.message ||
            "Failed to update the status. Please try again.",
          redirectPath: "",
        });
      } finally {
        setRowUpdatingId(null);
      }
    },
    [user_id, fetchStudentList, translations],
  );

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      let response;

      // Rows for the checked student_ids. Both payloads send the row's
      // `user_id` under the key `student_id` (the API expects the
      // numeric user id, not the 10-digit admission-style student_id).
      const selectedRows = data.filter((row) =>
        selectedStudentIds.includes(row.student_id),
      );
      const payloadStudentId = (row) => Number(row.user_id ?? row.student_id);

      if (viewMode) {
        /* ── UPDATE: schoolFees/groupAssignEdit ──
                   ONE request. The array is wrapped in `upadate_group`
                   (misspelled server-side — must match exactly).
                   `updated_by` sits outside the array.                */
        response = await AxiosInstance.post(GROUP_ASSIGN_EDIT_URL, {
          upadate_group: selectedRows.map((row) => ({
            assigned_id: Number(row.assign_id),
            student_id: payloadStudentId(row),
            group_id: Number(formData.groupType),
          })),
          updated_by: Number(user_id),
        });
      } else {
        /* ── CREATE: schoolFees/group-assign ──
                   `academic_id` (not academic_year_id).
                   `student_id` is an ARRAY of user_ids.               */
        response = await AxiosInstance.post(GROUP_ASSIGN_URL, {
          school_id: Number(school_id),
          academic_id: Number(academic_year_id),
          class_id: Number(selectedClass),
          section_id: String(selectedSection),
          medium: formData.medium || viewMedium || "",
          student_id: selectedRows.map(payloadStudentId),
          group_id: Number(formData.groupType),
          created_by: Number(user_id),
        });
      }

      const ok = Boolean(response?.data?.status || response?.data?.success);

      if (ok) {
        setSelectedStudentIds([]);
        fetchStudentList(); // refresh so new state shows
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message ||
            (viewMode
              ? "Fee group updated successfully!"
              : "Fee assigned successfully!"),
          redirectPath: "/user/listAssignFees",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            translations.labels?.somethingWrong ||
            "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting fee assign:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          translations.labels?.somethingWrong ||
          "Failed to assign fee. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate("/user/listAssignFees");

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) navigate(redirectPath);
  };

  const handleSearchEnter = useCallback((value) => {
    setApiSearch(value || "");
    // setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // ── Client-side search (API returns all students) ──
  const filteredData = useMemo(() => {
    if (!apiSearch) return data;
    const term = apiSearch.toLowerCase();
    return data.filter(
      (row) =>
        (row.student_name || "").toLowerCase().includes(term) ||
        String(row.student_id).includes(term),
    );
  }, [data, apiSearch]);

  // ── Table columns ──
  const columns = useMemo(
    () => [
      // checkbox column: create screen only (no bulk-assign on edit)
      ...(viewMode
        ? []
        : [
            {
              accessorKey: "select",
              header: "",
              size: 50,
              enableSorting: false,
              enableColumnFilter: false,
              Header: () => (
                <input
                  type="checkbox"
                  checked={isAllCurrentPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = isSomeCurrentPageSelected;
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleToggleAll();
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    cursor: "pointer",
                  }}
                />
              ),
              Cell: ({ row }) => (
                <input
                  type="checkbox"
                  checked={selectedStudentIds.includes(row.original.student_id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleToggleStudent(row.original.student_id);
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    cursor: "pointer",
                  }}
                />
              ),
            },
          ]),
      {
        accessorKey: "student_id",
        header: translations.labels?.applicationId || "Application ID",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "student_name",
        header: translations.labels?.studentName || "Student Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "group_fees",
        header: translations.labels?.groupFees || "Group Fees",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "assigned_status",
        header: translations.labels?.groupStatus || "Assigned Status",
        enableSorting: true,
        enableColumnFilter: true,
        // Fixed width so the column/filter doesn't reflow as the
        // selected filter option's label length changes.
        size: 180,
        minSize: 180,
        maxSize: 180,
        // Native MRT select filter (same mechanism as SecondaryAdmissionList's
        // Gender/Status columns) — value flows through the controlled
        // `columnFilters` state, synced into `assignStatus` for the fetch.
        filterVariant: "select",
        filterSelectOptions: assignStatusOptions,
        Cell: ({ row }) => {
          const r = row.original;
          // Add screen: static badge only. The per-row unassign
          // dropdown is an edit-screen (view mode) feature.
          if (!viewMode) {
            return <AssignedStatusBadge assigned={r.is_group} />;
          }
          const current =
            r.row_status != null ? Number(r.row_status) : STATUS_UNASSIGN;
          const busy = rowUpdatingId === r.student_id;
          // no assign_id -> nothing to toggle
          const disabled = busy || !r.assign_id;
          return (
            <select
              value={current}
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleRowStatusChange(r, Number(e.target.value))}
              style={{
                padding: "6px 12px",
                borderRadius: "var(--border-radius-sm, 4px)",
                border: "1px solid var(--color-dark-grey, #ccc)",
                fontSize: "var(--font-size-sm, 13px)",
                backgroundColor: disabled ? "#f0f0f0" : "#fff",
                color: disabled ? "#aaa" : "#333",
                cursor: disabled ? "not-allowed" : "pointer",
                minWidth: 110,
                outline: "none",
              }}
            >
              <option value={STATUS_ASSIGNED}>
                {translations.labels?.assigned || "Assigned"}
              </option>
              <option value={STATUS_UNASSIGN}>
                {translations.labels?.unassign || "Unassign"}
              </option>
            </select>
          );
        },
      },
    ],
    [
      translations,
      viewMode,
      selectedStudentIds,
      isAllCurrentPageSelected,
      isSomeCurrentPageSelected,
      handleToggleAll,
      handleToggleStudent,
      assignStatusOptions,
      rowUpdatingId,
      handleRowStatusChange,
    ],
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {!viewMode
              ? translations.labels?.createFeeAssign || "Create Fee Assign"
              : translations.labels?.editFeeAssign || "Edit Fee Assign"}
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>{translations.labels?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations.labels?.success || "Success"
            : translations.labels?.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {!viewMode
              ? translations.labels?.createFeeAssign || "Create Fee Assign"
              : translations.labels?.editFeeAssign || "Edit Fee Assign"}
          </h1>
        </div>

        <form
          onSubmit={(e) => {
            // Only the Assign/Update button may submit this form.
            // MRT's column-filter buttons have no type attribute,
            // so they default to type="submit"; Enter inside the
            // filter text field also submits implicitly. Both would
            // otherwise run validateForm() and pop the
            // "select at least one student" error.
            e.preventDefault();
          }}
          onKeyDown={(e) => {
            // swallow Enter unless it's on a real submit button
            if (e.key === "Enter" && e.target?.tagName !== "BUTTON") {
              e.preventDefault();
            }
          }}
        >
          <div className="row">
            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.class || "Class"}
                name="filterClass"
                value={selectedClass}
                onChange={handleClassChange}
                options={classOptions}
                placeholder={translations.labels?.selectClass || "Select Class"}
                disabled={viewMode}
                required
                error={errors.class}
              />
            </div>

            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.section || "Section"}
                name="filterSection"
                value={selectedSection}
                onChange={handleSectionChange}
                options={sectionOptions}
                placeholder={
                  translations.labels?.selectSection || "Select Section"
                }
                disabled={!selectedClass || viewMode}
                required
                error={errors.section}
              />
            </div>
          </div>

          {/* ── Assign Group Fees dropdown (create screen only) ── */}
          {!viewMode && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 12,
              }}
            >
              <div style={{ width: 250 }}>
                <CustomDropdown
                  name="groupType"
                  value={formData.groupType}
                  onChange={(value) => handleDropdownChange("groupType", value)}
                  options={groupTypeOptions}
                  placeholder={
                    groupTypeLoading
                      ? translations.labels?.loading || "Loading..."
                      : translations.labels?.selectGroupType ||
                        "Assign Group Fees"
                  }
                  required
                  error={errors.groupType}
                  isDisabled={groupTypeLoading}
                />
              </div>
            </div>
          )}

          {/* ── Student List Table ── */}
          <div className={styles.tableSection}>
            <CustomMRTTable
              columns={columns}
              data={filteredData}
              loading={tableLoading}
              // pagination={pagination}
              rowCount={filteredData.length}
              placeholder={
                translations?.labels?.searchNameCode || "Search by name..."
              }
              enablePagination={false}
              setSearchInput={setSearchInput}
              onSearchEnter={handleSearchEnter}
              // onPaginationChange={setPagination}
              searchInput={searchInput}
              enableSearch={false}
              enableFiltering={true}
              manualPagination={false}
              columnFilters={columnFilters}
              onColumnFiltersChange={setColumnFilters}
            />
          </div>

          <div className={styles.buttonContainer}>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {viewMode
                ? translations.labels?.back || "Back"
                : translations.labels?.cancel || "CANCEL"}
            </CustomButton>

            {/* Assign button: create screen only.
                            Edit screen manages status per-row via the
                            Assigned Status dropdown, so no Update button. */}
            {!viewMode && (
              <CustomButton
                type="button"
                variant="contained"
                className={styles.submitButton}
                fullWidth={false}
                loading={submitting}
                onClick={handleSubmit}
                loadingText={translations.labels?.submitting || "Submitting..."}
              >
                {translations.labels?.assign || "Assign"}{" "}
                {selectedStudentIds.length > 0 &&
                  `(${selectedStudentIds.length})`}
              </CustomButton>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateFeeAssign;
